import React, { useEffect, useState } from 'react';
import './PaymentPopup.css';
import billService from '../../services/bill.service';
import socketService, { PaymentSuccessData } from '../../services/socket.service';

interface PaymentPopupProps {
  isOpen: boolean;
  billId: string;
  expAt: number; // expiration timestamp in milliseconds
  qr?: string; // QR code image URL
  onClose: () => void;
  onPaymentSuccess?: () => void; // Callback when payment is verified
}

const PaymentPopup: React.FC<PaymentPopupProps> = ({ isOpen, billId, expAt, qr, onClose, onPaymentSuccess }) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyCountdown, setVerifyCountdown] = useState(0);
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);

  // Lắng nghe payment-success qua socket để cập nhật realtime
  useEffect(() => {
    if (!isOpen) return;

    const handlePaymentSuccess = (data: PaymentSuccessData) => {
      console.log('[PaymentPopup] Received payment-success event:', data);
      // Đảm bảo đúng bill hiện tại
      if (!data || data.billId !== billId) return;

      setVerifyMessage('Thanh toán thành công!');
      // Đợi 2s rồi đóng popup & callback
      setTimeout(() => {
        if (onPaymentSuccess) {
          onPaymentSuccess();
        }
        onClose();
      }, 2000);
    };

    socketService.onPaymentSuccess(handlePaymentSuccess);

    return () => {
      socketService.offPaymentSuccess(handlePaymentSuccess);
    };
  }, [isOpen, billId, onClose, onPaymentSuccess]);

  useEffect(() => {
    if (!isOpen) return;

    // Calculate remaining time
    const updateTimeRemaining = () => {
      const now = Date.now();
      const remaining = Math.max(0, expAt - now);
      setTimeRemaining(remaining);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [isOpen, expAt]);

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Countdown for verify button (10 seconds)
  useEffect(() => {
    if (verifyCountdown > 0) {
      const timer = setTimeout(() => {
        setVerifyCountdown(verifyCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [verifyCountdown]);

  const handleVerifyPayment = async () => {
    if (verifyCountdown > 0) {
      return; // Prevent spam
    }

    setIsVerifying(true);
    setVerifyMessage(null);
    setVerifyCountdown(10); // Start 10 second countdown

    try {
      // Fetch bill status to check if payment is completed
      const statusResponse = await billService.getBillStatus(billId);
      
      if (statusResponse.success) {
        if (statusResponse.status === 'completed') {
          setVerifyMessage('Thanh toán thành công!');
          // Call success callback after a short delay
          setTimeout(() => {
            if (onPaymentSuccess) {
              onPaymentSuccess();
            }
            onClose();
          }, 2000);
        } else {
          setVerifyMessage('Chưa nhận được thanh toán. Vui lòng thử lại sau.');
          setIsVerifying(false);
        }
      } else {
        setVerifyMessage(statusResponse.message || 'Không thể kiểm tra trạng thái thanh toán. Vui lòng thử lại.');
        setIsVerifying(false);
      }
    } catch (error) {
      console.error('Check payment status error:', error);
      setVerifyMessage('Có lỗi xảy ra. Vui lòng thử lại.');
      setIsVerifying(false);
    }
  };

  // Reset state when popup closes
  useEffect(() => {
    if (!isOpen) {
      setIsVerifying(false);
      setVerifyCountdown(0);
      setVerifyMessage(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="payment-popup-overlay" onClick={onClose}>
      <div className="payment-popup" onClick={(e) => e.stopPropagation()}>
        <div className="payment-popup-header">
          <h2>Thanh toán</h2>
          <button className="payment-popup-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="payment-popup-content">
          {/* QR Code */}
          <div className="payment-qr-container">
            {qr ? (
              <div className="payment-qr-code">
                <img 
                  src={qr} 
                  alt="QR Code thanh toán" 
                  className="payment-qr-image"
                  onError={(e) => {
                    // Fallback to billId text if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = 'block';
                    }
                  }}
                />
                <div className="payment-bill-id" style={{ display: 'none' }}>
                  {billId}
                </div>
              </div>
            ) : (
              <div className="payment-qr-code">
                <div className="payment-bill-id">{billId}</div>
                <div className="payment-qr-label">Mã đơn hàng</div>
              </div>
            )}
          </div>

          {/* Time remaining */}
          <div className="payment-timer">
            <div className="payment-timer-label">Thời gian còn lại:</div>
            <div className="payment-timer-value">{formatTime(timeRemaining)}</div>
          </div>

          {/* Instructions */}
          <div className="payment-instructions">
            <h3>Hướng dẫn thanh toán:</h3>
            <ol>
              <li>Quét mã QR ở trên để thanh toán</li>
              <li>Không chỉnh sửa số tiền và nội dung khi chuyển tiền</li>
            </ol>
          </div>

          {/* Note */}
          <div className="payment-note">
            <p>
              <strong>Lưu ý:</strong> Đơn hàng sẽ tự động hủy sau khi hết thời gian thanh toán.
              Vui lòng thanh toán trong thời gian quy định.
            </p>
          </div>

          {/* Verify Payment Button */}
          <div className="payment-verify-section">
            <button
              className={`payment-verify-btn ${isVerifying ? 'verifying' : ''} ${verifyCountdown > 0 ? 'disabled' : ''}`}
              onClick={handleVerifyPayment}
              disabled={isVerifying || verifyCountdown > 0}
            >
              {isVerifying ? (
                'Đang xác minh...'
              ) : verifyCountdown > 0 ? (
                `Vui lòng đợi ${verifyCountdown}s`
              ) : (
                'Bấm vào đây nếu bạn đã thanh toán'
              )}
            </button>
            {verifyMessage && (
              <div className={`verify-message ${verifyMessage.includes('thành công') ? 'success' : 'error'}`}>
                {verifyMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPopup;

